import React, { useEffect, useState } from 'react';
import {
    Typography, Table, Button, Empty
} from 'antd';
import {
    EyeOutlined,
    CheckCircleOutlined, CloseCircleOutlined, SyncOutlined
} from '@ant-design/icons';
import ApiClient from "../api";
import AssessmentModal from "../components/AssessmentModal";

const api_client = new ApiClient();
const { Title, Text } = Typography;

/* ── Status badge ─────────────────────────────────────────── */
const StatusTag = ({ status }) => {
    const map = {
        ENDED:   { color: '#79A7A5', bg: 'rgba(121,167,165,0.1)', icon: <CheckCircleOutlined />, label: 'Completed' },
        RUNNING: { color: '#f0a500', bg: 'rgba(240,165,0,0.1)',   icon: <SyncOutlined spin />,   label: 'Running'   },
        FAILED:  { color: '#ff4d4f', bg: 'rgba(255,77,79,0.1)',   icon: <CloseCircleOutlined />, label: 'Failed'    },
    };
    const cfg = map[status] || { color: '#6B8585', bg: 'rgba(107,133,133,0.1)', icon: null, label: status };

    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.color}33`,
            borderRadius: 6, padding: '2px 10px', fontSize: 11,
            fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.5px',
        }}>
            {cfg.icon} {cfg.label}
        </span>
    );
};

/* ── Main component ────────────────────────────────────────── */
const MyCases = ({wsMessages}) => {
    const [cases, setCases] = useState([]);
    const [loading, setLoading]  = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const fetchCases = async () => {
        setLoading(true);
        try {
            const data = await api_client.get_mycases_unread(localStorage.getItem('email'));
            setCases(data || []);
        } finally {
            setLoading(false);
        }
    };

    // When a new WS message arrives — re-fetch
    useEffect(() => {
        fetchCases();
    }, [wsMessages]);

    // On mount
    useEffect(() => {
        fetchCases();
    }, []);

    const handleView = async(record) => {
        let ai_assessment = await api_client.getaiassessment(record.stack_id);
        console.log('Assessment data', { ...record, assessment_data: ai_assessment.assessment_data })
        setSelected({ ...record, assessment_data: ai_assessment.assessment_data });
        setModalOpen(true);
    };

    /* ── Table columns ── */
    const columns = [
        {
            title: 'Insurer',
            dataIndex: 'selected_insurer',
            key: 'selected_insurer',
            render: (v) => <Text style={{ color: '#F0F8F7', fontSize: 13 }}>{v}</Text>,
        },
        {
            title: 'Policy',
            dataIndex: 'selected_policy',
            key: 'selected_policy',
            render: (v) => (
                <span style={{ fontFamily: 'monospace', color: '#79A7A5', fontSize: 12,
                    background: 'rgba(121,167,165,0.06)', border: '1px solid rgba(121,167,165,0.15)',
                    borderRadius: 4, padding: '2px 8px' }}>
                    {v}
                </span>
            ),
        },
        {
            title: 'IP NO',
            dataIndex: 'ip_no',
            key: 'ip_no',
            render: (v) => <Text style={{ color: '#9DBDBB', fontFamily: 'monospace', fontSize: 12 }}>{v}</Text>,
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (v) => (
                <Text style={{ color: '#6B8585', fontFamily: 'monospace', fontSize: 12 }}>
                    {v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                </Text>
            ),
        },
        {
            title: 'Claimed',
            dataIndex: 'claimed_amount',
            key: 'claimed_amount',
            align: 'right',
            render: (v) => (
                <Text style={{ color: '#F0F8F7', fontFamily: 'monospace', fontSize: 12 }}>
                    {v != null ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—'}
                </Text>
            ),
        },
        {
            title: 'Disallowed',
            dataIndex: 'disallowed_amount',
            key: 'disallowed_amount',
            align: 'right',
            render: (v) => (
                <Text style={{ color: v > 0 ? '#ff4d4f' : '#6B8585', fontFamily: 'monospace', fontSize: 12 }}>
                    {v != null ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—'}
                </Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (v) => <StatusTag status={v} />,
        },
        {
            title: '',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                    style={{
                        background: 'transparent',
                        border: '1px solid rgba(121,167,165,0.25)',
                        color: '#79A7A5',
                        borderRadius: 6,
                        fontSize: 12,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <div style={{ maxWidth: 960 }}>
            <Title level={2} style={{ color: 'var(--text-primary)', fontFamily: 'Space Grotesk', marginBottom: 4 }}>
                My Cases
            </Title>
            <Text style={{ color: '#3D5454', fontSize: 13, display: 'block', marginBottom: 24 }}>
                Unread adjudication results awaiting your review
            </Text>

            <AssessmentModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                caseData={selected}
                onMarkedRead={(stackId) => {
                    setCases(prev => prev.filter(c => c.stack_id !== stackId));
                }}
                showMarkAsReadButton
            />

            <Table
                dataSource={cases}
                columns={columns}
                rowKey="id"
                loading={{ spinning: loading, indicator: <SyncOutlined spin style={{ color: '#79A7A5', fontSize: 24 }} /> }}
                locale={{ emptyText: <Empty description={<Text style={{ color: '#3D5454' }}>No unread cases</Text>} /> }}
                pagination={{ pageSize: 10, style: { marginTop: 16 } }}
                style={{ background: 'transparent' }}
                onRow={(record) => ({
                    style: { cursor: 'default' },
                    onMouseEnter: e => e.currentTarget.style.background = 'rgba(121,167,165,0.04)',
                    onMouseLeave: e => e.currentTarget.style.background = 'transparent',
                })}
            />

            {/* Ant Design table dark overrides */}
            <style>{`
                .ant-table { background: transparent !important; }
                .ant-table-thead > tr > th {
                    background: #161C1C !important;
                    color: #3D5454 !important;
                    border-bottom: 1px solid rgba(121,167,165,0.12) !important;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    font-family: monospace;
                }
                .ant-table-tbody > tr > td {
                    background: transparent !important;
                    border-bottom: 1px solid rgba(121,167,165,0.06) !important;
                    color: #F0F8F7;
                }
                .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
                .ant-pagination-item a { color: #6B8585 !important; }
                .ant-pagination-item-active { border-color: #79A7A5 !important; }
                .ant-pagination-item-active a { color: #79A7A5 !important; }
                .ant-pagination-prev button, .ant-pagination-next button { color: #6B8585 !important; }
            `}</style>
        </div>
    );
};

export default MyCases;